// libraries
import React from 'react'
import PropTypes from 'prop-types'
import { startCase, toLower, uniq } from 'lodash'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Grid, Typography, Divider, Box, Stack } from '@mui/material'
import { useTheme } from '@mui/styles'

// generic components
import Input from 'components/generic/form/Input'
import PrintInput from 'components/generic/printable/components/PrintInput'
import PrintLocationInfo from 'components/generic/printable/components/PrintLocationInfo'
import SignatureDateLine from 'components/generic/printable/components/SignatureDateLine'
import SimpleMaterialsTable from 'components/generic/printable/table/SimpleMaterialsTable'

// helper & utils
import { calculateTotalWeight, calculateTotalValue } from 'utils/data-manipulation/common'
import { formatNumber } from 'utils/helpers'
import { PACKAGE_OPTIONS, PAYMENT_METHODS_VALUES, PAYMENT_OPTIONS_OPTIONS } from 'utils/constants'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'

// styled components
import { Title } from './StyledElements'
import { Detail } from './components/Detail'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

const PrintablePurchaseOrder = ({ orderData, materials, pricePrepend }) => {
  const theme = useTheme()
  const methods = useFormContext()
  const isMixed = uniq(materials.map(m => m.um?.toUpperCase())).length > 1

  const { yards: yardName } = useSelector(state => state.auth)
  const { settings } = useSelector(state => state.settings)

  const supplierLocation = orderData?.supplier?.locations?.find(
    location => location.id === orderData?.purchaseOrderLocationId,
  )

  const formattedSupplierData = {
    name: orderData?.supplier?.displayName,
    address1: supplierLocation?.address1 || '',
    address2: supplierLocation?.address2 || '',
    city: supplierLocation?.city || '',
    state: supplierLocation?.state || '',
    zip: supplierLocation?.zip || '',
    phone: supplierLocation?.phone || '',
  }

  return (
    <>
      <Grid container>
        <Grid item xs={2.5}>
          <PrintLocationInfo title1="Bought From" location1={formattedSupplierData} />
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={6} pl={2}>
          <Typography variant="h5">Order Details</Typography>
          <Grid mt={1} container>
            <PrintInput name="order.effectiveDate" label="EFFECTIVE DATE" control={methods.control} />
            <PrintInput name="order.expirationDate" label="EXPIRY DATE" control={methods.control} />
            <PrintInput name="order.packageType" label="PACKAGE TYPE" control={methods.control} />
            <PrintInput name="order.receivingYard" label="RECEIVING YARD" control={methods.control} />
            <PrintInput name="order.pickupDelivery" label="PICKUP/DELIVERY" control={methods.control} />
            <PrintInput name="blank" label=" " control={methods.control} />
            <PrintInput name="paymentTermId" label="PAYMENT TERMS" control={methods.control} />
            <PrintInput name="paymentMethod" label="PAYMENT METHOD" control={methods.control} />
            <PrintInput name="paymentOptions" label="PAYMENT OPTIONS" control={methods.control} />
          </Grid>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={3} pl={2}>
          <Title>&nbsp;</Title>
          <Stack>
            <Box>
              Total Weight: {isMixed ? 'Mixed' : `${formatNumber(calculateTotalWeight(materials))} ${materials[0]?.um}`}
            </Box>
            <Box>
              Total Value: {pricePrepend}
              {formatNumber(calculateTotalValue(materials).toFixed(2))}
            </Box>
          </Stack>
        </Grid>
      </Grid>
      <Box pt={3}>
        <Stack direction="row" mb={1}>
          <Typography variant="h5" gutterBottom color="textSecondary">
            Materials
          </Typography>
        </Stack>
        <SimpleMaterialsTable materials={materials} type="PURCHASE_ORDER" pricePrepend={pricePrepend} />
      </Box>
      <Box pt={3}>
        <Stack direction="row" mb={1}>
          <Typography variant="h5" gutterBottom color="textSecondary">
            Notes
          </Typography>
        </Stack>
        <Input
          name="notes"
          disabled
          control={methods.control}
          fullWidth
          multiline
          variant="standard"
          sx={{
            'textarea.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
            },
          }}
          InputProps={{
            disableUnderline: true,
          }}
        />
      </Box>
      {settings?.contractSettings?.purchaseOrders?.purchaseOrderDisclaimer && (
        <Grid item xs={12} mt={2}>
          <Typography
            variant="body2"
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {settings?.contractSettings?.purchaseOrders?.purchaseOrderDisclaimer || ''}
          </Typography>
        </Grid>
      )}
      <Divider orientation="horizontal" flexItem />
      <Grid container justifyContent="space-between" alignItems="center" flexWrap="nowrap">
        <Grid item mt={3} xs={6}>
          <Title>{startCase(toLower(yardName || ''))}</Title>
          <SignatureDateLine date={new Date().getTime()} />
        </Grid>
        <Grid item mt={3} xs={6}>
          <Title>{formattedSupplierData?.name}</Title>
          <SignatureDateLine />
        </Grid>
      </Grid>
    </>
  )
}

// a lot of the HTML and styling here came directly from design
// so to save some time its not fully converted to JSX and is a bit ugly
const PrintablePurchaseOrderV2 = ({ orderData, materials, pricePrepend }) => {
  const theme = useTheme()
  //  const isMixed = uniq(materials.map(m => m.um?.toUpperCase())).length > 1

  const { contractLocation } = orderData
  const { details, settings, defaultYardUm } = useSelector(st => st.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  const formattedSupplierData = {
    name: orderData?.supplier?.displayName,
    address1: contractLocation?.address1 || '',
    address2: contractLocation?.address2 || '',
    city: contractLocation?.city || '',
    state: contractLocation?.state || '',
    zip: contractLocation?.zip || '',
    phone: contractLocation?.phone || '',
  }

  const formattedMaterials = materials.map(material => ({
    ...material,
    um: disableUmDropdowns ? defaultYardUm : material.um,
  }))

  const { paymentTermsOptions } = usePaymentTerms({})

  return (
    <>
      <div
        style={{
          display: 'flex',
          padding: theme.spacing(1),
          border: '1px solid black',
        }}
      >
        <div style={{ flexBasis: '50%' }}>
          <strong>Bought from:</strong>
          <div>
            <Typography>{formattedSupplierData?.name}</Typography>
            {formattedSupplierData?.address1 && (
              <>
                <Typography>{formattedSupplierData?.address1}</Typography>
                <Typography>
                  {formattedSupplierData?.city}, {formattedSupplierData?.state} {formattedSupplierData?.zip}
                </Typography>
              </>
            )}
          </div>
        </div>
        <div style={{ flexBasis: '50%' }}>
          <strong>Sold To:</strong>
          <div>
            <Typography>{details?.addressName}</Typography>
            <Typography>{details?.address}</Typography>
            <Typography>
              {details?.city}, {details?.state} {details?.zip}
            </Typography>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: '1em',
          padding: theme.spacing(1),
          border: '1px solid black',
          lineHeight: '2em',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <Detail label="Effective Date" data={dateFormatter(orderData?.order?.effectiveDate)} />
        <Detail label="Expiration Date" data={dateFormatter(orderData?.order?.expirationDate)} />
        <Detail
          label="Package Type"
          data={PACKAGE_OPTIONS.find(option => option.id === orderData?.order?.packageType)?.name}
        />
        <Detail label="Receiving Yard" data={orderData?.order?.receivingYard} />
        <Detail label="Pickup/Delivery" data={orderData?.order?.pickupDelivery} />
        <Detail
          label="Payment Terms"
          data={paymentTermsOptions.find(option => option.id === orderData?.paymentTermId)?.name}
        />
        <Detail label="Payment Method" data={PAYMENT_METHODS_VALUES[orderData?.paymentMethod?.toUpperCase()] || ''} />
        <Detail label="Currency" data={orderData?.currency} />
        <Detail
          label="Payment Options"
          data={PAYMENT_OPTIONS_OPTIONS.find(option => option.id === orderData?.paymentOptions)?.name}
        />
        <Detail label="Customer SO#" data={orderData?.customerSONumber} />
      </div>

      <div style={{ border: '1px solid black', marginTop: '1em' }}>
        <SimpleMaterialsTable materials={formattedMaterials} type="PURCHASE_ORDER" pricePrepend={pricePrepend} />
      </div>

      <div
        style={{
          marginTop: '1em',
          padding: theme.spacing(1),
          border: '1px solid black',
          width: '100%',
        }}
      >
        <strong>Notes:</strong>
        <p style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{orderData?.notes}</p>
      </div>

      {settings?.contractSettings?.purchaseOrders?.purchaseOrderDisclaimer && (
        <Grid item xs={12} mt={2}>
          <Typography
            variant="body2"
            style={{
              fontSize: '8px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {settings?.contractSettings?.purchaseOrders?.purchaseOrderDisclaimer || ''}
          </Typography>
        </Grid>
      )}

      <div style={{ display: 'flex' }}>
        <div
          style={{
            flexBasis: '50%',
            display: 'flex',
            marginTop: '3em',
            marginRight: '24px',
          }}
        >
          <div
            style={{
              marginRight: theme.spacing(1),
              flexBasis: '50%',
              borderTop: '1px solid black',
            }}
          >
            Signature
          </div>
          <div style={{ flexBasis: '50%', borderTop: '1px solid black' }}>Date</div>
        </div>
        <div style={{ flexBasis: '50%', display: 'flex', marginTop: '3em' }}>
          <div
            style={{
              marginRight: theme.spacing(1),
              flexBasis: '50%',
              borderTop: '1px solid black',
            }}
          >
            Signature
          </div>
          <div style={{ flexBasis: '50%', borderTop: '1px solid black' }}>Date</div>
        </div>
      </div>
    </>
  )
}

PrintablePurchaseOrder.propTypes = {
  orderData: PropTypes.object.isRequired,
  materials: PropTypes.arrayOf(PropTypes.object).isRequired,
  pricePrepend: PropTypes.string,
}

PrintablePurchaseOrder.defaultProps = {
  pricePrepend: '$',
}

PrintablePurchaseOrderV2.propTypes = PrintablePurchaseOrder.propTypes
PrintablePurchaseOrderV2.defaultProps = PrintablePurchaseOrder.defaultProps

export default PrintablePurchaseOrderV2
